import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  HostListener,
  inject,
  signal,
  computed,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { take } from 'rxjs';

import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { LANGUAGES } from '@app/core/constants/language.constants';
import { LanguageService } from '@services/language.service';
import { MemberService } from '@services/member.service';
import { ConfirmationService } from '@services/confirmation.service';
import { Member } from '@models/member.model';
import { ElearnioLanguageSpanModule } from '@shared/elearnio-language-icon/elearnio-language-span.module';
import { GraphicOptionCardComponent } from '@shared/graphic-option-card/graphic-option-card.component';
import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { InfoBubbleModule } from '@shared/info-bubble';
import { InfoBubbleType } from '@shared/info-bubble/info-bubble.model';
import { type2img } from '@helper/h5p-icons-helper';

import {
  COURSE_INTENT_OPTIONS,
  TARGET_AUDIENCE_OPTIONS,
  CourseIntent,
  TargetAudience,
  GuidedCourseCreationFormValue,
  OptionConfig,
  CourseOutline,
  MOCK_COURSE_OUTLINE,
} from './guided-course-creation.constants';

type DialogPage = 'form' | 'outline';

@Component({
  selector: 'app-guided-course-creation-dialog',
  templateUrl: './guided-course-creation-dialog.component.html',
  styleUrls: ['./guided-course-creation-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatSelectModule,
    MatFormFieldModule,
    MatRadioModule,
    MatCheckboxModule,
    TranslateModule,
    TooltipButtonComponent,
    ElearnioLanguageSpanModule,
    GraphicOptionCardComponent,
    InfoBubbleModule,
  ],
})
export class GuidedCourseCreationDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<GuidedCourseCreationDialogComponent>,
  );
  private readonly fb = inject(FormBuilder);
  private readonly languageService = inject(LanguageService);
  private readonly memberService = inject(MemberService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly intentOptions: readonly OptionConfig<CourseIntent>[] =
    COURSE_INTENT_OPTIONS;
  readonly audienceOptions: readonly OptionConfig<TargetAudience>[] =
    TARGET_AUDIENCE_OPTIONS;
  readonly availableLanguages = Object.values(LANGUAGES).filter(
    lang => lang !== LANGUAGES.ZZ,
  );
  readonly infoBubbleType = InfoBubbleType.Info;

  readonly currentPage = signal<DialogPage>('form');
  readonly outline = signal<CourseOutline | null>(null);
  readonly isLoading = signal(false);
  private mockTimers: ReturnType<typeof setTimeout>[] = [];

  member: Member | undefined;
  readonly termsAlreadyAccepted = signal(false);

  readonly form = this.fb.nonNullable.group({
    topic: ['', Validators.required],
    language: [
      this.languageService.getCurrentLanguage() as LANGUAGES,
      Validators.required,
    ],
    intent: ['' as CourseIntent | '', Validators.required],
    audience: [[] as TargetAudience[], Validators.required],
  });

  readonly formSummary = computed(() => {
    const raw = this.form.getRawValue();
    const intentOption = this.intentOptions.find(o => o.id === raw.intent);
    const audienceLabels = raw.audience
      .map(a => {
        const option = this.audienceOptions.find(o => o.id === a);
        return option ? this.translateService.instant(option.titleKey) : a;
      })
      .join(', ');

    return {
      intentLabel: intentOption
        ? this.translateService.instant(intentOption.titleKey)
        : '',
      audienceLabel: audienceLabels,
    };
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.clearMockTimers());

    this.memberService
      .current()
      .pipe(take(1))
      .subscribe(member => {
        this.member = member as Member;
        this.termsAlreadyAccepted.set(this.member.aiTermsAgreedTo);
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.currentPage() === 'outline' || this.isLoading()) {
      event.preventDefault();
    }
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  isAudienceSelected(id: TargetAudience): boolean {
    return this.form.controls.audience.value.includes(id);
  }

  toggleAudience(id: TargetAudience): void {
    const current = this.form.controls.audience.value;
    const next = current.includes(id)
      ? current.filter(a => a !== id)
      : [...current, id];
    this.form.controls.audience.setValue(next);
  }

  onGenerate(): void {
    if (!this.form.valid || !this.member?.aiTermsAgreedTo) return;

    this.isLoading.set(true);
    this.setSummary(MOCK_COURSE_OUTLINE);

    // Simulate API delay for outline details — will be replaced with real API call
    this.mockTimers = [
      setTimeout(() => this.setOutline(MOCK_COURSE_OUTLINE), 5500),
    ];
  }

  setSummary(outline: CourseOutline): void {
    this.outline.set(outline);
    this.currentPage.set('outline');
  }

  setOutline(outline: CourseOutline): void {
    this.outline.set(this.enrichOutlineIcons(outline));
    this.isLoading.set(false);
  }

  onBack(): void {
    this.clearMockTimers();
    this.currentPage.set('form');
    this.outline.set(null);
    this.isLoading.set(false);
  }

  onCreateCourse(): void {
    const raw = this.form.getRawValue();
    const formValue: GuidedCourseCreationFormValue = {
      topic: raw.topic,
      language: raw.language,
      intent: raw.intent as CourseIntent,
      audience: raw.audience,
    };

    if (this.member?.hasDirtyAttributes) {
      this.member.save().pipe(take(1)).subscribe();
    }

    this.dialogRef.close({ formValue, outline: this.outline() });
  }

  onClose(): void {
    if (this.currentPage() === 'outline') {
      this.confirmationService
        .showConfirmDialogModal(
          'common.ok',
          'common.confirmation.unsaved_changes',
        )
        .pipe(take(1))
        .subscribe(confirmed => {
          if (confirmed) {
            this.dialogRef.close();
          }
        });
    } else {
      this.dialogRef.close();
    }
  }

  toggleSection(index: number): void {
    const current = this.outline();
    if (!current) return;

    const updated = {
      ...current,
      sections: current.sections.map((section, i) =>
        i === index ? { ...section, expanded: !section.expanded } : section,
      ),
    };
    this.outline.set(updated);
  }

  onTermsAndConditionsChange(event: MatCheckboxChange): void {
    if (this.member) {
      this.member.aiTermsAgreedTo = event.checked;
      if (this.member.hasDirtyAttributes) {
        this.member.save().pipe(take(1)).subscribe();
      }
    }
  }

  private clearMockTimers(): void {
    this.mockTimers.forEach(id => clearTimeout(id));
    this.mockTimers = [];
  }

  private enrichOutlineIcons(outline: CourseOutline): CourseOutline {
    return {
      ...outline,
      sections: outline.sections.map(section => ({
        ...section,
        modules: section.modules.map(mod => ({
          ...mod,
          iconPath: type2img(mod.moduleType),
        })),
      })),
    };
  }
}
